import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { parseServerDateTime } from "@/lib/globalFormatterHelper";
import { fetchRequestWrapper } from "@/lib/utilities";
import { isExpiryLive } from "@/components/widgets/tradeActionSummary/helper/cellRenderers/MainTableActionSummaryESLevel/ExpiresCellRenderer/expiryCountdownHelpers";
import clockStore from "@/store/clockStore";
import featureFlagStore from "@/store/featureFlagStore";
import tradeDataStore from "@/store/tradeDataStore";

// LQC-2366 - extend-expiry for the OP blotter's edit-expiry popover, against the
// LQC-2166 endpoint. Fire and forget: the response body is not used and the
// refreshed trade arrives over MegaPipe's TradeUpdated, so "did not throw" is
// the only success signal. A 204 would make a truthiness check read as failure.

export interface IUpdateTradeExpiryPayload {
  esid: string;
  expiryType: string;
  // Total seconds, paired with expiryPeriodType "s" (expiryDurationHelper).
  expiryPeriod: number;
  expiryPeriodType: string;
}

export async function updateTradeExpiry(
  payload: IUpdateTradeExpiryPayload,
  getAccessTokenSilently: Function,
): Promise<{ success: boolean }> {
  try {
    await fetchRequestWrapper(
      {
        url: "/Trade/Execution/Edit/Expiry",
        method: "PUT",
        objToSend: payload,
      },
      getAccessTokenSilently,
    );
    return { success: true };
  } catch {
    // fetchRequestWrapper has already toasted and refreshed the trade.
    return { success: false };
  }
}

// ---------------------------------------------------------------------------
// LQC-2173 - expiry reconcile (MOCK)
// ---------------------------------------------------------------------------
// If a trade's expiry passes and the status has not followed, the client tells
// the backend so. Deliberately framed as a REPORT, not a command: the backend
// re-evaluates using its own clock, which is what keeps client clock skew out
// of the decision.
//
// It sweeps the store rather than firing from the cell renderer, because AG Grid
// only mounts renderers for visible rows - a safety net that only covers the
// rows you happen to be looking at is not a safety net.

// Long enough for the backend's own enforcement job to win the race in normal
// operation, short enough to matter. In a healthy system this fires ~never, so
// treat every occurrence as a signal the backend job is behind.
export const EXPIRY_RECONCILE_GRACE_MS = 5000;
const SWEEP_INTERVAL_MS = 5000;

// Once per esid per session. Module scope on purpose: it must survive the cell
// unmounting as rows scroll in and out of view.
const reportedEsids = new Set<string>();

// Pure: which trades have lapsed without the status following. Exported for
// tests - the sweep itself is just this plus the call.
export function returnTradesNeedingExpiryReconcile(
  trades: ExecutionStrategy[] | undefined,
  nowMs: number,
  alreadyReported: Set<string>,
): ExecutionStrategy[] {
  if (!trades?.length) return [];
  return trades.filter((trade) => {
    if (!trade?.esid || alreadyReported.has(trade.esid)) return false;
    if (!isExpiryLive(trade.status)) return false;
    const expiryEnd = parseServerDateTime(trade.expiryEnd);
    if (!expiryEnd) return false;
    return nowMs - expiryEnd.getTime() >= EXPIRY_RECONCILE_GRACE_MS;
  });
}

export async function reconcileExpiredTrade(payload: {
  esid: string;
  expiredAt: string;
}): Promise<{ success: boolean }> {
  console.log(
    "[MOCK] reconcileExpiredTrade -> POST /ExecutionStrategy/ExpiryReconcile",
    payload,
  );
  return { success: true };
}

export function runExpiryReconcileSweep(nowMs: number): void {
  // Gated on the countdown flag as well as the column: the countdown is what
  // notices a lapse in the first place, so with it off the front end is not in
  // the business of reporting expiry at all.
  const flags = featureFlagStore.getState();
  if (!flags.tradeExpiryColumn || !flags.tradeExpiryCountdown) return;

  const trades = tradeDataStore.getState().tradeData;
  for (const trade of returnTradesNeedingExpiryReconcile(
    trades,
    nowMs,
    reportedEsids,
  )) {
    reportedEsids.add(trade.esid);
    reconcileExpiredTrade({
      esid: trade.esid,
      expiredAt: String(trade.expiryEnd),
    });
  }
}

let unsubscribeFromClock: (() => void) | null = null;
let lastSweepMs = 0;

export function startExpiryReconcileSweep(): void {
  if (unsubscribeFromClock) return;
  unsubscribeFromClock = clockStore.subscribe(({ nowMs }) => {
    if (nowMs - lastSweepMs < SWEEP_INTERVAL_MS) return;
    lastSweepMs = nowMs;
    runExpiryReconcileSweep(nowMs);
  });
}

export function stopExpiryReconcileSweep(): void {
  unsubscribeFromClock?.();
  unsubscribeFromClock = null;
}

// Test seam - the session-scoped dedupe is otherwise unreachable.
export function clearReportedExpiries(): void {
  reportedEsids.clear();
  lastSweepMs = 0;
}
